"use strict";
const { getConfiguration, getCachedConfigurations } = require('@nuskin/configuration-sdk');
const axios = require("axios").default;
const contentstack = require('./contentstack/contentstack');
const Product = require("./product");
const CustomerTypes = require('./models/customerTypes');
const ProductStatus = require("./models/productStatus");
const { mapBackOrderDate, mapAvailableQuantity, mapChildSKU, mapChildSKUBundle } = require('./equinox-helpers');
const { productNotFoundInterceptor } = require('./equinox-helpers/interceptors');
const { getProducts } = require('./graph-ql');
const productTypes = {
  kit: 'kit',
  bundle: 'bundle'
};

/** @type {*} */
const ProductData = {
  /**
   * @param {string[]} skus
   * @param {string} locale
   * @param {string} market
   */
  getProductData: async function (skus, locale, market) {
    const localeMarket = `${locale}_${market}`;

    // USE THESE TWO LINES FOR LOCAL TESTING INSTEAD OF const config alone
    // let runConfig = { country: market, language: locale, environment: 'test', clientId: '66408e5bca6b4a59b2c7fc62b8c00a9f' };
    // const config = (await getConfiguration(['Equinox_Markets'], runConfig)).Equinox_Markets;
    const config = (await getConfiguration(['Equinox_Markets'])).Equinox_Markets;
    return (config.MySite_graphql_active_list.includes(market)) ?
      await getProducts(skus, market, locale, config) :
      await this.getProductFromLegacy(skus, localeMarket);
  },

  getProductFromEquinox: async function (skus, locale, config) {
    const response = await this.searchEquinoxProduct(skus, locale, config);

    if (response.data.product) {
      return await this.eqProductMapper(response.data.product, skus);
    }

    return {
      status: 400,
      messages: [response.data.message],
      data: { count: 0, products: [] }
    }
  },

  /**
   *
   * @param {string[]} skus
   * @param {string} locale
   * @param {ConfigMap} config
   * @returns
   */
  searchEquinoxProduct: async function (skus, locale, config) {
    let skuFilter = [];

    const shoppingContext = window && window.aem && window.aem.ShoppingContext && window.aem.ShoppingContext.getShoppingContext();

    if ((shoppingContext && shoppingContext.context === 'storefront')
      || window.location.hostname.includes(".mynuskin.com")) {
      config.API_Base_URLs = config.MySite_API_Base_URLs
    }

    if (Array.isArray(skus)) {
      skus.forEach((sku) => {
        skuFilter.push(`index_key_productId="${sku}" OR index_key_skuId="${sku}"`)
      })
    } else {
      //all featured products
      skuFilter.push(`index_key_productId="${skus}" OR index_key_skuId="${skus}"`)
    }

    const filter = skuFilter.join(" OR ")

    const url = `${config.API_Base_URLs}/orchestrationservices/storefront/catalogs/search/`;
    const href = `${url}?filter='\\\\''${encodeURI(filter)}'\\''`;
    axios.interceptors.response.use((res) => res, productNotFoundInterceptor);
    const response = await axios.request({
      method: 'get',
      url: href,
      params: {
        locale,
        storeId: config.store_id
      },
      headers: this.getEquinoxRequestHeaders({ shoppingContext }),
      responseType: 'json',
      withCredentials: true
    });

    axios.interceptors.response.eject(productNotFoundInterceptor);
    return response;
  },

  getEquinoxRequestHeaders(option) {
    const header = {
      'Content-Type': 'application/json'
    }

    if (option.shoppingContext && option.shoppingContext.overrides) {
      header.showwholesalepricing = option.shoppingContext.overrides.showWholeSalePricing;
    }

    return header;
  },

  getProductFromLegacy: async function (skus, locale) {
    let marketConfig = await contentstack.getAEMConfig();

    if (marketConfig === null) {
      marketConfig = {
        aws_url: "https://testapi.cloud.nuskin.com/"
      }
    }

    const lambdaUrl = `${marketConfig.aws_url}/productData/v1`;
    const payload = { locale, skus };

    return await axios.request({
      method: 'post',
      url: lambdaUrl,
      data: payload,
      headers: this.getLegacyRequestHeaders(marketConfig),
      responseType: 'json'
    });
  },

  /**
   * @param {contentstack.MarketConfig} marketConfig
   */
  getLegacyRequestHeaders: function (marketConfig) {
    return {
      'Content-Type': 'application/json',
      client_id: marketConfig.checkout_client_id,
      client_secret: marketConfig.checkout_client_secret
    };
  },

  /**
   * Get available product quantity
   * If backOrdered === true, use backOrderedQty
   * else use atpQty
   * @todo deprecate this and try using equinox-helpers/availableQuantity.js
   * @param {obj} inventory
   * @returns {number} qty
   */
  getAvailableQty: function (inventory) {
    if (!inventory || !Object.hasOwnProperty.call(inventory, 'backOrdered')
      || !Object.hasOwnProperty.call(inventory, 'atpQty') || !Object.hasOwnProperty.call(inventory, 'backOrderedQty')) {
      return 0;
    }

    if (inventory.backOrdered) {
      return inventory.backOrderedQty;
    }

    return inventory.atpQty;
  },

  /**
   * Get backorder date for normal, kit, bundle product
   * @param {*} product
   * @returns {number} backOrderDate
   */
  getBackOrderDate: function (product) {

    if ((product.type === productTypes.kit || product.type === productTypes.bundle) && product.childSkus) {

      //return max epoch time since childSkus already mapped in fetchChildSkus
      if (product.childSkus.some(sku => sku.inventory && sku.inventory.backOrderDate)) {
        return Math.max(...product.childSkus.filter(sku => sku.inventory && sku.inventory.backOrdered).map(sku => (sku.inventory && sku.inventory.backOrderDate) || 0));
      }
    }

    return mapBackOrderDate(product);
  },

  /**
   * Map product variant
   * @todo remove unnecessary fields and do code refactoring
   * @param {*} eqVariant
   * @returns Array<Product>
   */
  eqProductVariantMapper: function (eqVariant) {

    let imageURL = eqVariant.properties.imageURL ? eqVariant.properties.imageURL : '';
    let thumbnailImage = imageURL ? imageURL + '?width=40' : ''


    const { eventName, eventLabels, computedPrice, defaultProductPrice, CVPrice, PVPrice } = this.getEqProductPromotions(eqVariant);
    const productPrice = eventName ? defaultProductPrice : eqVariant.priceFacets["Regular Price"];
    const discountedPrice = eventName ? computedPrice : eqVariant.priceFacets["Regular Price"];
    const productCVPrice = eventName ? CVPrice : eqVariant.priceFacets.CV;
    const productPVPrice = eventName ? PVPrice : eqVariant.priceFacets.PV;
    const wholeSalePrice = eventName ? defaultProductPrice : eqVariant.priceFacets["Wholesale Price"];

    return {
      "sku": eqVariant.identifier,
      "globalProductID": eqVariant.identifier,
      "title": eqVariant.properties.name,
      "country": eqVariant.properties.market,
      "language": "en",
      "shortDescr": eqVariant.properties.description,
      "longDescr": eqVariant.properties.productDetailsDescription,
      "fullImage": imageURL,
      "imageAltText": eqVariant.properties.name,
      "productCarouselImages": [
        {
          "contentType": "img",
          "src": "/content/products/24/00/35/24003529/jcr:content/fullImage.imgw.1280.1280.png",
          "alt": ""
        }
      ],
      "thumbnail": thumbnailImage,
      "ingredients": eqVariant.properties.ingredients,
      "benefits": eqVariant.properties.benefits,
      "usage": eqVariant.properties.usage,
      "resources": eqVariant.properties.resources,
      "videos": "",
      "movie": "",
      "youtube": "",
      "salesEventText": "",
      "contentSection": [
        {
          "sectionContent": `<div class="contentSections">
            <div class="contentSection section">
              <div>\n<h3>BENEFITS</h3>\n
                <div>\n
                  <div class="sectionContent parsys">
                    <div class="text parbase section">\n
                      <p>
                        Green tea has been used in China for thousands of years for its health-preserving and
                        revitalizing power. Modern science has identified powerful antioxidants in green tea
                        called polyphenols. One group of polyphenols called catechins is particularly potent
                        and is highly effective at neutralizing free radicals at the cellular level. Catechins
                        support healthy cell function while providing antioxidant protection to critical cell
                        structures such as DNA. Clinical studies show catechins interfere with the production
                        of certain enzymes involved in cell damage. Additionally, catechins have been demonstrated
                        to cause a thermogenic effect that enhances the body s metabolic rate. Tegreen 97<sup>®</sup>
                        from Pharmanex is a proprietary, highly concentrated extract of the catechins found
                        in green tea. Tegreen 97<sup>®</sup> is one of the most potent antioxidant supplements
                        on the market with a 97 percent polyphenol content, 65% catechins. Each Tegreen 97<sup>®</sup>
                        capsule contains the catechin equivalent of approximately seven cups of green tea and is
                        99.5 percent caffeine free.
                      </p>\r\n\n
                    </div>\n\n
                  </div>\n\n
                </div>\n
              </div>\n
            </div>\n
            <div class="contentSection section">
              <div>\n<h3>USAGE</h3>\n
                <div>\n
                  <div class="sectionContent parsys">
                    <div class="text parbase section">\n
                      <p>As a dietary supplement, take one (1) to four (4) capsules daily; preferably one (1)
                      to two (2) each morning and evening with food. Store in a cool, dry place.</p>\r\n\n
                    </div>\n\n
                  </div>\n\n
                </div>\n
              </div>\n
            </div>\n
            <div class="contentSection section">
              <div>\n<h3>INGREDIENTS</h3>\n
                <div>\n
                  <div class="sectionContent parsys">
                    <div class="text parbase section">\n
                      <table border="1" cellspacing="1" cellpadding="0" width="360">
                        <tbody>\r\n
                          <tr>\r\n
                            <td><b>Nutritional Facts</b></td>\r\n
                            <td><b>Amount</b></td>\r\n<td><b>% Daily Value</b></td>\r\n
                          </tr>\r\n
                          <tr>\r\n
                            <td colspan="3"><b>Serving Size: 1 Capsule<br>\r\nServings Per Container: 120</b></td>\r\n
                          </tr>\r\n
                          <tr>\r\n
                            <td width="55%">Green tea leaf (20:1) Extract (<i>Camellia sinensis</i></td>\r\n
                            <td width="20%">250 mg</td>\r\n<td width="25%">&nbsp;</td>\r\n
                          </tr>\r\n
                          <tr>\r\n<td width="55%" colspan="3">* Daily Value not established</td>\r\n</tr>\r\n
                          <tr>\r\n
                            <td width="55%" colspan="3">
                              <b>Other Ingredients: Gelatin, Silicon Dioxide,
                              Microcrystalline Cellulose, Magnesium Stearate.</b>
                            </td>\r\n</tr>\r\n
                        </tbody>\r\n
                      </table>\r\n
                      <p>&nbsp;</p>\r\n\n
                      </div>\n\n
                    </div>\n\n
                  </div>\n
                </div>\n
              </div>\n
            </div>\n`
        }
      ],
      "scanQualified": eqVariant.properties.scanQualifiedCount,
      "availableQuantity": this.getAvailableQty(eqVariant.inventoryProperties),
      "maxQuantity": 999,
      "points": "",
      "cv": productCVPrice,
      "pv": productPVPrice,
      "priceType": "WRTL",
      "price": discountedPrice,
      "priceMap": {
        "WRTL": productPrice,
        "WADW-WRTL": productPrice,
        "WADR": productPrice, //retail ADR (subscription) price
        "RTL": productPrice,
        "WWHL": wholeSalePrice,
        "WADW": discountedPrice,//wholesale ADR (subscription price)
        "WHL": wholeSalePrice
      },
      "cvMap": {
        "WWHL": productCVPrice,
        "WADW": productCVPrice,
        "WHL": productCVPrice
      },
      "pvMap": {
        "WWHL": productPVPrice,
        "WADW": productPVPrice,
        "WHL": productPVPrice
      },
      "orderTypes": this._setOrderType(eqVariant.properties),
      "custTypes": this.switchCustType(eqVariant.properties.customerTypes),
      "division": eqVariant.properties.division,
      "backOrderDate": this.getBackOrderDate(eqVariant),
      "locallyProduced": false,
      "agelocme": null,
      "count": "",
      "flavor": "",
      "size": eqVariant.properties.size,
      "shade": "",
      "status": this.switchStatusFromEquinox(eqVariant.properties.productStatus),
      "variantType": "Other",
      "variantDropdownLabel": eqVariant.properties.variantLabel || "",
      "variantDropdownPlaceholder": "Select Type",
      "variantsLabel": eqVariant.properties.variantSelectLabel || "",
      "groupOffer": false,
      "personalOffer": false,
      "savedEventName": eventName,
      "salesLabel": eventLabels,
      "eventName": eventName,
      "sizeWeight": '',
      "nettoWeight": "",
      "searchScore": 0,
      "isExclusive": eqVariant.properties.isExclusive || false,
      "marketAttributes": {
        "discount": true,
        "redeem": true,
        "earn": true
      },
      "restrictedMarkets": [],
      "addOns": [],
      "equinoxProductId": eqVariant.identifier,
      "properties": eqVariant.properties
    };
  },

  /**
   * Get EQ product promotions
   * @param {*} product
   * @returns {obj} promotion
   */
  getEqProductPromotions(product) {

    if (!Array.isArray(product.promotion)) {
      return {
        eventLabels: "",
        computedPrice: 0,
        defaultProductPrice: product.priceFacets ? product.priceFacets["Regular Price"] : 0,
        eventName: null
      }
    }

    const defaultProductPrice = product.totalValue ? product.totalValue.originalPrice : 0;

    const computedPrice = product.totalValue && product.totalValue.priceAfterDiscount
      ? product.totalValue.priceAfterDiscount
      : 0;

    let CVPrice = 0,
      PVPrice = 0;

    if (product.totalValue && product.totalValue.priceFacets) {
      const productPriceFacets = product.totalValue.priceFacets;
      CVPrice = productPriceFacets.CV && productPriceFacets.CV.CVAfterDiscount ? productPriceFacets.CV.CVAfterDiscount : 0;
      PVPrice = productPriceFacets.PV && productPriceFacets.PV.PVAfterDiscount ? productPriceFacets.PV.PVAfterDiscount : 0;
    }

    let eventName = "";
    const eventLabels = [];

    product.promotion.map(promotion => {
      eventName = promotion.offerId;
      eventLabels.push(promotion.message);
      return promotion;
    });

    return {
      eventLabels: eventLabels.join(','),
      defaultProductPrice,
      computedPrice,
      eventName,
      CVPrice,
      PVPrice
    }
  },

  eqProductMapper: async function (productDataResponse, skus) {
    let count = 0;
    const products = [];

    for (const productData of productDataResponse) {
      try {
        let variants = {};
        let product = (productData.type && (productData.type === "kit" || productData.type === "bundle")) ? productData : productData.sku[count];
        let imageURL = product.properties.imageURL ? product.properties.imageURL : '';
        let thumbnailImage = imageURL ? imageURL + '?width=40' : '';
        let productTitle = productData.properties.name;
        let productSku = product.identifier

        if (productData.sku && productData.sku.length > 1) {
          // exclude base product from variants
          productData.sku.filter(v => v.identifier.substring(2, 4) !== "55").map(variant => {
            variants[variant.identifier] = this.eqProductVariantMapper(variant);
            return variant;
          });
          const productArr = productData.sku.filter(p => p.default === true)
          productTitle = productArr[0].properties.name;
          productSku = productArr[0].identifier
        }

        const {
          eventName,
          eventLabels,
          computedPrice,
          defaultProductPrice,
          CVPrice,
          PVPrice
        } = this.getEqProductPromotions(product);
        const productPrice = eventName ? defaultProductPrice : product.priceFacets["Regular Price"];
        const discountedPrice = eventName ? computedPrice : product.priceFacets["Wholesale Price"];
        const productCVPrice = eventName ? CVPrice : product.priceFacets.CV;
        const productPVPrice = eventName ? PVPrice : product.priceFacets.PV;
        const wholeSalePrice = eventName ? discountedPrice : product.priceFacets["Wholesale Price"];


        product.childSkus = await this.fetchChildSkus(product);
        product.availableQuantity = mapAvailableQuantity(product);
        product = {
          ...product,
          "sku": productSku,
          "globalProductID": productData.identifier,
          "title": productTitle,
          "country": product.properties.market,
          "language": "en",
          "shortDescr": productData.properties.description,
          "longDescr": productData.properties.productDetailsDescription,
          "fullImage": imageURL,
          "imageAltText": productData.properties.name,
          "productCarouselImages": [
            {
              "contentType": "img",
              "src": "/content/products/24/00/35/24003529/jcr:content/fullImage.imgw.1280.1280.png",
              "alt": ""
            }
          ],
          "thumbnail": thumbnailImage,
          "ingredients": productData.properties.ingredients,
          "benefits": productData.properties.benefits,
          "usage": productData.properties.usage,
          "resources": product.properties.resources,
          "videos": "",
          "movie": "",
          "youtube": "",
          "salesEventText": "",
          "contentSection": [
            {
              "sectionContent": "<div class=\"contentSections\"></div>\n"
            }
          ],
          "scanQualified": productData.properties.scanQualifiedCount,
          "maxQuantity": 999,
          "points": "",
          "cv": productCVPrice,
          "pv": productPVPrice,
          "priceType": "WRTL",
          "price": discountedPrice,
          "priceMap": {
            "WRTL": productPrice, //regular | retail price
            "WADW-WRTL": productPrice,
            "WADR": productPrice, //retail ADR (subscription price)
            "RTL": productPrice,
            "WADW": discountedPrice,//wholesale ADR (subscription price)
            "WHL": wholeSalePrice,
            "WWHL": wholeSalePrice
          },
          "cvMap": {
            "WWHL": productCVPrice,
            "WADW": productCVPrice,
            "WHL": productCVPrice
          },
          "pvMap": {
            "WWHL": productPVPrice,
            "WADW": productPVPrice,
            "WHL": productPVPrice
          },
          "orderTypes": this._setOrderType(product.properties),
          "custTypes": this.switchCustType(product.properties.customerTypes),
          "division": productData.properties.division,
          "backOrderDate": this.getBackOrderDate(product),
          "locallyProduced": false,
          "agelocme": null,
          "count": "",
          "flavor": "",
          "size": product.properties.size,
          "shade": "",
          "status": this.switchStatusFromEquinox(product.properties.productStatus),
          "variantType": "Other",
          "variantDropdownLabel": product.properties.variantLabel || "",
          "variantDropdownPlaceholder": "Select Type",
          "variantsLabel": productData.properties.variantSelectLabel || "",
          "groupOffer": false,
          "personalOffer": false,
          "savedEventName": eventName,
          "salesLabel": eventLabels,
          "eventName": eventName,
          "sizeWeight": '',
          "nettoWeight": "",
          "variants": variants,
          "searchScore": 0,
          "isExclusive": product.properties.isExclusive || false,
          "marketAttributes": {
            "discount": true,
            "redeem": true,
            "earn": true
          },
          "restrictedMarkets": [],
          "addOns": [],
          "inventory": product.inventory || product.properties.inventoryStatus || "", //inventory label,
          "equinoxProductId": productData.identifier,
          "properties": product.properties,
          equinox: {
            sku: {
              exists: this.mapEquinoxSKUExists(productData.exists)
            }
          }
        };

        products.push(new Product(product));
      } catch (err) {
        console.error('Unable to map the product', productData.identifier, ':', err);
        console.error('Product info:', productDataResponse, skus);
        continue;
      }
    }

    let data = {
      products: this._sortProductsBySku(skus, products),
      count: productDataResponse.length
    };
    return {
      data: {
        "status": 200,
        "messages": [],
        "data": data
      }
    };
  },

  mapEquinoxSKUExists: function (exists) {
    // this could be avoided if only we are using response mapper
    // for equinox products that exists.
    // @todo: clean-up
    if (exists === undefined) {
      return true;
    }

    return exists;
  },

  /**
   * fetchChildSkus make requests to Equinox to get the SKUs of a kit.
   * @param {*} product
   */
  fetchChildSkus: async function (product) {
    if (product.type === productTypes.kit) {
      const config = getCachedConfigurations(['Equinox_Markets']).Equinox_Markets;
      const { skukits } = product.properties;
      const { locale } = product.tempProperties[0];

      if (!skukits) {
        return [];
      }

      const kits = skukits.split(',').map(kit => {
        const [sku, qty] = kit.split('~');
        return { sku, qty };
      });
      const skus = kits.map(k => k.sku);
      const res = await this.searchEquinoxProduct(skus, locale, config);
      const childSkus = [
        {
          productId: product.identifier,
          skuId: product.identifier,
          type: 'SKUKIT',
          availableChannels: product.properties.availableChannels
        },
        ...res.data.product.map(p => mapChildSKU(kits, p))
      ];

      return childSkus;
    }

    if (product.type === productTypes.bundle) {
      const config = getCachedConfigurations(['Equinox_Markets']).Equinox_Markets;
      const { bundlemandatoryproductids } = product.properties;
      const { locale } = product.tempProperties[0];

      if (!bundlemandatoryproductids) {
        return [];
      }

      const bundles = bundlemandatoryproductids.split(',').map(kit => {
        const [sku, qty] = kit.split('~');
        return { sku, qty };
      });

      const skus = bundles.map(k => k.sku);
      const res = await this.searchEquinoxProduct(skus, locale, config);
      const childSkus = [
        {
          productId: product.identifier,
          skuId: product.identifier,
          type: 'BUNDLE',
          availableChannels: product.properties.availableChannels
        },
        ...res.data.product.map(p => mapChildSKUBundle(bundles, p))
      ];

      return childSkus;
    }

    return [];
  },

  /**
   *
   * Map status from equinox to  legacy
   *
   * @param {*} status
   * @returns {string}  productStatus
   */
  switchStatusFromEquinox: function (status) {
    const { equinoxStatus } = ProductStatus;
    let newStatus = '';
    switch (status.toLowerCase()) {
      case equinoxStatus.SELLABLE:
        newStatus = ProductStatus.ReleasedForSale;
        break;
      case equinoxStatus.PREVIEW_PRODUCT:
        newStatus = ProductStatus.NotReleasedForSale;
        break;
      case equinoxStatus.DISCONTINUED:
        newStatus = ProductStatus.Discontinued;
        break;
      case equinoxStatus.STOPPED:
        newStatus = ProductStatus.Discontinued;
        break;
      case equinoxStatus.REPLACEMENT:
        newStatus = ProductStatus.NotReleasedForSale;
        break;
      default:
        newStatus = ProductStatus.NotReleasedForSale;
        break;
    }

    return newStatus;
  },

  /**
   * map customer types from string to code<number>
   * @param {*} eqCustomerTypes
   * @returns
   */
  switchCustType: function (eqCustomerTypes) {
    if (!eqCustomerTypes) {
      return "";
    }

    const custTypes = eqCustomerTypes.split(',').map(customerType => customerType.trim().toLowerCase());

    let newCustType = [];
    if (custTypes.includes(CustomerTypes.BrandAffiliate)) {
      newCustType.push(CustomerTypes.properties.BRAND_AFFILIATE.code);
    }

    if (custTypes.includes(CustomerTypes.Retail)) {
      newCustType.push(CustomerTypes.properties.RETAIL.code);
    }

    if (custTypes.includes(CustomerTypes.Preferred) || custTypes.includes(CustomerTypes.PreferredCustomer)) {
      newCustType.push(CustomerTypes.properties.PREFERRED.code);
    }

    return newCustType.toString()
  },

  /**
   * Sorts the product by sku
   * @param {Array} skus - the skus arrangment which the sorting will base out of
   * @param {Array} products - this products to be sorted
   */

  _sortProductsBySku: function (skus, products) {

    if (!skus || !products || products.length === 1) {
      return products
    }

    const sortedProducts = products.sort(function (a, b) {
      return skus.indexOf(a.sku) - skus.indexOf(b.sku);
    });

    return sortedProducts

  },

  /**
   *
   * @param {*} availableChannels
   *
   * This function is use to convert arsPhone,web,kiosk,mobile,subscription
   * to orderType object
   *  "adr”
      "order"
      "zpfc"
      "zadp"
      "ars"
      "kiosk"
      "mobile"
      "preferred customer"
      "retail"
      "web"
      "web display"
   */
  _setOrderType: function (properties) {
    let orderTypeArr = {
      "adr": false,
      "order": false,
      "zpfc": false,
      "zadp": false,
      "ars": false,
      "kiosk": false,
      "mobile": false,
      "preferred customer": false,
      "retail": false,
      "web": false,
      "web display": false
    };
    let availableChannelsArr = properties.availableChannels.split(',')

    availableChannelsArr.forEach(channel => {
      if (channel == 'arsPhone')
        orderTypeArr.ars = true
      if (channel == 'web') {
        orderTypeArr.order = true
        orderTypeArr.web = true
      }
      if (channel == 'kiosk')
        orderTypeArr.kiosk = true
      if (channel == 'mobile')
        orderTypeArr.mobile = true
      if (channel == 'subscription')
        orderTypeArr.adr = true

    });
    if (properties.retail)
      orderTypeArr.retail = true
    if (properties.prefferredCustomer)
      orderTypeArr['preferred customer'] = true

    return orderTypeArr;
  }
}

module.exports = ProductData;
